import { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowRight, Mail, Lock, User, Building2 } from "lucide-react";

export default function AuthDialog({ open, onOpenChange, defaultTab = "login" }) {
  const { login, signup } = useAuth();
  const [tab, setTab] = useState(defaultTab);
  const [loading, setLoading] = useState(false);

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [signupForm, setSignupForm] = useState({ name: "", email: "", company: "", password: "", confirm: "" });

  const handleLogin = (e) => {
    e.preventDefault();
    if (!loginForm.email || !loginForm.password) {
      toast.error("Please enter email and password.");
      return;
    }
    setLoading(true);
    try {
      login(loginForm);
      toast.success("Welcome back.");
      onOpenChange(false);
      setLoginForm({ email: "", password: "" });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = (e) => {
    e.preventDefault();
    if (!signupForm.name || !signupForm.email || !signupForm.password) {
      toast.error("Name, email and password are required.");
      return;
    }
    if (signupForm.password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    if (signupForm.password !== signupForm.confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      signup(signupForm);
      toast.success(`Account created. Welcome, ${signupForm.name}.`);
      onOpenChange(false);
      setSignupForm({ name: "", email: "", company: "", password: "", confirm: "" });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="auth-dialog" className="sm:max-w-[460px] p-0 gap-0 rounded-none border-[#E2E8F0]">
        <div className="bg-[#0B2341] text-white px-8 py-7">
          <DialogHeader>
            <div className="overline mb-2" style={{ color: "#16A34A" }}>VPCL Infra Account</div>
            <DialogTitle className="font-heading text-2xl font-semibold tracking-tight">
              {tab === "login" ? "Welcome back" : "Create your account"}
            </DialogTitle>
            <DialogDescription className="text-white/70 text-sm mt-1">
              {tab === "login"
                ? "Access saved projects, enquiries and corporate downloads."
                : "Track your project enquiries and unlock private case studies."}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="p-8">
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 bg-[#F8F9FA] rounded-none p-0 h-auto border-b border-[#E2E8F0]">
              <TabsTrigger
                value="login"
                data-testid="tab-login"
                className="rounded-none data-[state=active]:bg-white data-[state=active]:text-[#0B2341] data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#16A34A] text-[#4A5568] py-3 text-xs uppercase tracking-[0.18em] font-medium"
              >
                Sign In
              </TabsTrigger>
              <TabsTrigger
                value="signup"
                data-testid="tab-signup"
                className="rounded-none data-[state=active]:bg-white data-[state=active]:text-[#0B2341] data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#16A34A] text-[#4A5568] py-3 text-xs uppercase tracking-[0.18em] font-medium"
              >
                Create Account
              </TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-6">
              <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                <IconField icon={Mail} label="Email">
                  <input
                    type="email"
                    autoComplete="email"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    className={inputCls}
                    placeholder="you@company.com"
                    data-testid="login-email"
                  />
                </IconField>
                <IconField icon={Lock} label="Password">
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    className={inputCls}
                    placeholder="••••••••"
                    data-testid="login-password"
                  />
                </IconField>
                <button type="submit" disabled={loading} className="btn-primary w-full group disabled:opacity-60" data-testid="login-submit">
                  {loading ? "Signing in..." : "Sign In"}
                  <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </button>
                <p className="text-center text-xs text-[#4A5568] mt-4">
                  New to VPCL Infra?{" "}
                  <button type="button" onClick={() => setTab("signup")} className="text-[#16A34A] font-medium hover:underline" data-testid="switch-to-signup">
                    Create an account
                  </button>
                </p>
              </form>
            </TabsContent>

            <TabsContent value="signup" className="mt-6">
              <form onSubmit={handleSignup} className="space-y-4" data-testid="signup-form">
                <IconField icon={User} label="Full Name">
                  <input
                    value={signupForm.name}
                    onChange={(e) => setSignupForm({ ...signupForm, name: e.target.value })}
                    className={inputCls}
                    placeholder="Your full name"
                    data-testid="signup-name"
                  />
                </IconField>
                <IconField icon={Mail} label="Email">
                  <input
                    type="email"
                    value={signupForm.email}
                    onChange={(e) => setSignupForm({ ...signupForm, email: e.target.value })}
                    className={inputCls}
                    placeholder="you@company.com"
                    data-testid="signup-email"
                  />
                </IconField>
                <IconField icon={Building2} label="Company (optional)">
                  <input
                    value={signupForm.company}
                    onChange={(e) => setSignupForm({ ...signupForm, company: e.target.value })}
                    className={inputCls}
                    placeholder="Organisation"
                    data-testid="signup-company"
                  />
                </IconField>
                <div className="grid grid-cols-2 gap-3">
                  <IconField icon={Lock} label="Password">
                    <input
                      type="password"
                      value={signupForm.password}
                      onChange={(e) => setSignupForm({ ...signupForm, password: e.target.value })}
                      className={inputCls}
                      placeholder="Min 6 chars"
                      data-testid="signup-password"
                    />
                  </IconField>
                  <IconField icon={Lock} label="Confirm">
                    <input
                      type="password"
                      value={signupForm.confirm}
                      onChange={(e) => setSignupForm({ ...signupForm, confirm: e.target.value })}
                      className={inputCls}
                      placeholder="Repeat"
                      data-testid="signup-confirm"
                    />
                  </IconField>
                </div>
                <button type="submit" disabled={loading} className="btn-primary w-full group disabled:opacity-60" data-testid="signup-submit">
                  {loading ? "Creating..." : "Create Account"}
                  <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </button>
                <p className="text-center text-xs text-[#4A5568] mt-2">
                  Already have an account?{" "}
                  <button type="button" onClick={() => setTab("login")} className="text-[#16A34A] font-medium hover:underline" data-testid="switch-to-login">
                    Sign In
                  </button>
                </p>
                <p className="text-center text-[10px] text-[#4A5568]/70 leading-relaxed">
                  Accounts are stored locally in your browser for demo purposes only.
                </p>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const inputCls =
  "w-full bg-white border border-[#E2E8F0] focus:border-[#0B2341] focus:ring-1 focus:ring-[#0B2341] focus:outline-none rounded-none pl-10 pr-4 py-3 text-sm text-[#0A1118] placeholder:text-[#4A5568]/60";

function IconField({ icon: Icon, label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-medium text-[#0B2341] uppercase tracking-[0.18em] mb-1.5">{label}</span>
      <div className="relative">
        <Icon size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#4A5568]" />
        {children}
      </div>
    </label>
  );
}
